import { db, firestore } from "./FirebaseService";
import { ref, onValue, push } from "firebase/database";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { getThreadPath } from "./MessagesService";
import { useMessagesStore } from "@/stores/useMessagesStore";

const VENDOR_ID = process.env.VUE_APP_VENDOR_ID;

/**
 * Sets up listener for search results
 * @param {string} uid
 * @param {Function} onSearchResultsChanged - Callback when search results change
 * @returns {Function} Cleanup function to stop listening
 */
export function subscribeToSearchResults(uid, onSearchResultsChanged) {
  const stopListening = onValue(ref(db, `panel_results/${VENDOR_ID}/${uid}`), (data) => {
    if (onSearchResultsChanged) {
      onSearchResultsChanged(data.exists() ? data.val() : null);
    }
  });

  return stopListening;
}

/**
 * Subscribes to the lead's liked_units subcollection. The callback fires
 * with a `{[unitId]: {unit_id, created_at}}` map shaped to match the old
 * `interested_units` API the store + components were using.
 * @param {string} uid
 * @param {(map: object) => void} onChange
 * @returns {Function} unsubscribe
 */
export function subscribeToLikedUnits(uid, onChange) {
  if (!uid) return () => {};
  const likedRef = collection(
    firestore, "vendors", VENDOR_ID, "leads", uid, "liked_units",
  );
  return onSnapshot(likedRef, (snap) => {
    const map = {};
    snap.docs.forEach((d) => {
      map[d.id] = d.data();
    });
    onChange(map);
  });
}

/**
 * Toggles whether or not the user is interested in the specified unit.
 * Writes/deletes the Firestore liked_units doc; the snapshot subscription
 * in the store updates `interestedUnits` from the resulting change.
 * @param {string} uid
 * @param {object} unit
 * @param {object} currentInterestedUnits - Current state of liked units
 */
export async function toggleInterest(uid, unit, currentInterestedUnits) {
  if (!uid) return;
  const chatId = useMessagesStore().ensureChatId();

  const unitPayload = { ...unit }
  delete unitPayload.link
  delete unitPayload.images

  const sysMsg = {
    role: 'system',
    payload: unitPayload,
    timestamp: Date.now(),
  }

  const interested = currentInterestedUnits[unit.stock_number];

  // Subcollection under the lead so all of a lead's likes are scoped under
  // their doc. Doc id is the unit's stock_number — the same value the
  // cloud-side `addLikedUnit` uses — so the on-toggle is idempotent and
  // the off-toggle can delete by id without a query.
  const likedUnitDocRef = doc(
    firestore,
    "vendors", VENDOR_ID, "leads", uid, "liked_units", unit.stock_number,
  );

  if (!interested) {
    setDoc(likedUnitDocRef, {
      unit_id: unit.stock_number,
      created_at: serverTimestamp(),
    });
    sysMsg.content = `The user is interested in this: ${JSON.stringify(unitPayload)}`
    sysMsg.action = "interested"
  } else {
    deleteDoc(likedUnitDocRef);
    sysMsg.content = `The user is no longer interested in this: ${JSON.stringify(unitPayload)}`
    sysMsg.action = "not_interested"
  }

  push(ref(db, getThreadPath(uid, chatId)), sysMsg);
}
